/**
 * 수입원천등록 PoC
 * - 단일 CRUD 그리드 · 회계연도별 localStorage
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import { showAlert } from 'utils/toastBridge';
import {
  SOURCE_TYPE_OPTIONS,
  FEE_KIND_OPTIONS,
  YN_OPTIONS,
  sourceTypeLabel,
  createSourceUid,
  emptySourceRow,
  loadSources,
  saveSources,
  nextSourceNo,
  filterSources,
} from 'income/incomeStore';

const GRID_ID = 'income-source-register';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export default function IncomeSourceRegister() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      fiscalYear: getCurrentFiscalYear(),
      sourceType: '',
      useYn: '',
    },
  });
  const { getValues, reset } = methods;

  const gridRef = useRef(null);

  const columns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '원천번호', field: 'sourceNo', size: 80, align: 'center' }),
      hiCol.edComboColumn({
        header: '유형',
        field: 'sourceType',
        size: 90,
        options: SOURCE_TYPE_OPTIONS,
        required: true,
      }),
      hiCol.edTextColumn({ header: '거래처코드', field: 'vendorCode', size: 90 }),
      hiCol.edTextColumn({ header: '거래처명', field: 'vendorName', size: 120 }),
      hiCol.edNumberColumn({ header: '계약금액', field: 'contractAmt', size: 100, align: 'right' }),
      hiCol.edNumberColumn({
        header: '청구일',
        field: 'invoiceDay',
        size: 70,
        align: 'center',
        title: '매월 청구일 (1~31)',
      }),
      hiCol.edTextColumn({ header: '사업명', field: 'projectName', size: 120 }),
      hiCol.edTextColumn({ header: '과목코드', field: 'subjectCode', size: 90 }),
      hiCol.edTextColumn({ header: '과목명', field: 'subjectName', size: 120 }),
      hiCol.edNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right' }),
      hiCol.edComboColumn({ header: '수수료종', field: 'feeKind', size: 90, options: FEE_KIND_OPTIONS }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: YN_OPTIONS }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptySourceRow({ useYn: 'Y', mod: 'A' }),
  });

  const dims = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || '').trim(),
    }),
    [getValues, tenantCode]
  );

  const handleSearch = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    const all = await loadSources(d);
    const filtered = filterSources(all, {
      sourceType: getValues('sourceType'),
      useYn: getValues('useYn'),
    });
    const rows = filtered.map((r) => ({
      ...r,
      sourceTypeNm: sourceTypeLabel(r.sourceType),
    }));
    gridRef.current?.setData?.(rows);
  }, [dims, getValues]);

  useEffect(() => {
    reset({ fiscalYear: getCurrentFiscalYear(), sourceType: '', useYn: '' });
  }, [reset]);

  const handleAdd = useCallback(async () => {
    const d = dims();
    const all = await loadSources(d);
    const uid = createSourceUid();
    const row = emptySourceRow({
      sourceUid: uid,
      sourceNo: nextSourceNo(all),
      useYn: 'Y',
      mod: 'A',
    });
    gridRef.current?.setData?.([...(gridRef.current?.getData?.() || []), row]);
  }, [dims]);

  const handleSave = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    const gridRows = gridRef.current?.getData?.() || [];
    const all = await loadSources(d);
    const byUid = new Map(all.map((r) => [r.sourceUid, r]));

    gridRows.forEach((row) => {
      const uid = row.sourceUid || createSourceUid();
      const prev = byUid.get(uid);
      const merged = {
        ...emptySourceRow(),
        ...(prev || {}),
        ...row,
        sourceUid: uid,
        sourceNo: row.sourceNo || prev?.sourceNo || nextSourceNo([...byUid.values()]),
        mod: prev ? (row.mod === 'D' ? 'D' : 'U') : row.mod || 'A',
      };
      // 청구일 1~31 범위 보정
      const day = Number(merged.invoiceDay);
      merged.invoiceDay = Number.isFinite(day) ? Math.min(31, Math.max(1, day)) : 1;
      byUid.set(uid, merged);
    });

    await saveSources(d, Array.from(byUid.values()));
    await handleSearch();
    showAlert('저장했습니다.', 'success');
  }, [dims, handleSearch]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'sourceType',
          label: '유형',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...SOURCE_TYPE_OPTIONS],
          width: '100px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'useYn',
          label: '사용',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...YN_OPTIONS],
          width: '80px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7', title: '조회 (F7)' },
      { id: 'add', label: '추가', icon: '➕', onClick: handleAdd, hotkey: 'F8', title: '추가 (F8)' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: () => grid.handleDelete?.(),
        hotkey: 'F9',
        variant: 'danger',
        title: '삭제 (F9)',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
        title: '저장 (F10)',
      },
    ],
    [handleSearch, handleAdd, handleSave, grid]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <Stack>
          <CollapsibleSearchPanel
            storageKey="income-source-register"
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Panel>
            <GridBody>
              <HiGridComponent
                ref={gridRef}
                columns={columns}
                showRowNumber
                rowNumberSize={48}
                enableColumnFilters
                enableContextMenu
                enableRowEditMenu
                enableCut
                enableFindReplace
                allowReplace
                enableHeaderMenu
                enableSelectionStats
                enableQuickExport
                exportFileName="수입원천"
                gridId={GRID_ID}
                prefsUserId={prefsUserId}
                {...(grid.contextMenuProps || {})}
              />
            </GridBody>
          </Panel>
        </Stack>
      </FormProvider>
    </PageContainer>
  );
}
